import { types as api } from '@mesa-labs/mesa-api';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders, useInvoiceApi } from './api';
import { RootState } from '../store';
import { vendorApi } from './vendor';
import { ExportFormatType } from '../slices/invoices';
import { Partners, UserAccountActivityType } from '@mesa-labs/mesa-api/dist/types';

export const invoicesApi = createApi({
  reducerPath: 'invoicesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.invoiceUrl,
    prepareHeaders,
  }),
  tagTypes: ['AllTimeInvoiceTotals', 'Invoices', 'InvoiceLineItems', 'InvoiceAdjustments', 'BulkAcceleratedPayout', 'SelectiveAcceleratedPayout', 'VendorClientTotals', 'PromotionalPayout', 'VendorInvoiceQuarantines'],
  endpoints: (build) => ({
    getInvoice: build.query<api.RedactedInvoiceResponse | undefined, { invoiceId: string }>({
      async queryFn(params, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getInvoice<api.InvoiceAggregateResponse>(params.invoiceId);
        return { data };
      },
    }),
    getAllInvoices: build.query<api.IPagedResults<api.RedactedInvoiceResponse>, api.InvoiceFilterParams>({
      async queryFn(params, thunkApi) {
        const externalInvoiceApi = useInvoiceApi(thunkApi.dispatch);
        const data = await externalInvoiceApi.getAllInvoices<api.RedactedInvoiceResponse>(params);

        const email = (thunkApi.getState() as RootState).auth.email;
        const vendorId = (thunkApi.getState() as RootState).auth.vendorId;

        if (email && vendorId) {
          thunkApi.dispatch(vendorApi.endpoints.logUserAccountActivity.initiate({ email, activity: UserAccountActivityType.ResourceGet, resource: 'invoices' }))
            .catch(() => { /* */ });
        }

        return { data };
      },
      providesTags: ['Invoices'],
    }),
    getAllInvoiceLineItems: build.query<api.IPagedResults<api.RedactedInvoiceLineItemResponse>, api.InvoiceLineItemFilterParams>({
      async queryFn(params, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getAllInvoiceLineItems<api.RedactedInvoiceLineItemResponse>(params);
        return { data };
      },
      providesTags: (result, _error, { vendorId, batchTransferId }) => [{ type: 'InvoiceLineItems', vendorId, batchTransferId, ...result }],
    }),
    getAllTimeInvoiceTotals: build.query<api.VendorBusinessTotalsResponse, { vendorId: api.UUID, partnerId?: Partners }>({
      async queryFn({ vendorId, partnerId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getVendorAllTimeInvoiceTotals<api.VendorBusinessTotalsResponse>(vendorId, {
          ...(partnerId ? { partnerId } : {}),
        });
        return { data };
      },
      providesTags: ['AllTimeInvoiceTotals']
    }),
    getAllInvoiceAdjustments: build.query<api.IPagedResults<api.RedactedInvoiceAdjustmentResponse>, api.InvoiceAdjustmentFilterParams>({
      async queryFn(params, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getAllAdjustments<api.RedactedInvoiceAdjustmentResponse>(params);
        return { data };
      },
      providesTags: (result, _error, { vendorId, batchTransferId }) => [{ type: 'InvoiceAdjustments', vendorId, batchTransferId, ...result }],
    }),
    getVendorClientTotals: build.query<api.VendorClientTotalsResponse, { vendorId: api.UUID }>({
      async queryFn(params, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getVendorClientTotals<api.VendorClientTotalsResponse>(params.vendorId);
        return { data };
      },
      providesTags: ['VendorClientTotals'],
    }),
    previewInstantPayout: build.query<api.AcceleratedPayoutResponse, { invoiceId: api.UUID }>({
      async queryFn({ invoiceId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewInstantPayout(invoiceId);
        return { data };
      },
    }),
    confirmInstantPayout: build.mutation<api.AcceleratedPayoutResponse, { invoiceId: api.UUID }>({
      async queryFn({ invoiceId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmInstantPayout(invoiceId);
        return { data };
      },
      invalidatesTags: ['Invoices', 'BulkAcceleratedPayout', 'SelectiveAcceleratedPayout', 'AllTimeInvoiceTotals'],
    }),
    previewBulkInstantPayout: build.query<api.BulkAcceleratedPayoutResponse, { vendorId: api.UUID }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewBulkInstantPayout(vendorId);
        return { data };
      },
      providesTags: ['BulkAcceleratedPayout'],
    }),
    confirmBulkInstantPayout: build.mutation<api.BulkAcceleratedPayoutResponse, { vendorId: api.UUID }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmBulkInstantPayout(vendorId);

        return { data };
      },
      invalidatesTags: ['Invoices', 'BulkAcceleratedPayout', 'AllTimeInvoiceTotals'],
    }),
    previewPayOnApprovalByRequest: build.query<api.AcceleratedPayoutResponse, { invoiceId: api.UUID }>({
      async queryFn({ invoiceId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewPayOnApprovalByRequest(invoiceId);
        return { data };
      },
    }),
    confirmPayOnApprovalByRequest: build.mutation<api.AcceleratedPayoutResponse, { invoiceId: api.UUID }>({
      async queryFn({ invoiceId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmPayOnApprovalByRequest(invoiceId);
        return { data };
      },
      invalidatesTags: ['Invoices', 'AllTimeInvoiceTotals'],
    }),
    previewSelectiveInstantPayout: build.query<api.SelectiveAcceleratedPayoutResponse, api.SelectiveAcceleratedPayoutRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewSelectiveInstantPayout(vendorId, request);
        return { data };
      },
      providesTags: ['SelectiveAcceleratedPayout'],
    }),
    confirmSelectiveInstantPayout: build.mutation<api.SelectiveAcceleratedPayoutResponse, api.SelectiveAcceleratedPayoutRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmSelectiveInstantPayout(vendorId, request);

        return { data };
      },
      invalidatesTags: ['Invoices', 'SelectiveAcceleratedPayout', 'AllTimeInvoiceTotals'],
    }),
    previewOnDemandPayout: build.query<api.AcceleratedPayoutResponse, { invoiceId: api.UUID }>({
      async queryFn({ invoiceId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewOnDemandPayout(invoiceId);
        return { data };
      },
    }),
    confirmOnDemandPayout: build.mutation<api.AcceleratedPayoutResponse, api.ConfirmOnDemandPayoutRequest & { invoiceId: api.UUID }>({
      async queryFn({ invoiceId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmOnDemandPayout(invoiceId, request);
        return { data };
      },
      invalidatesTags: ['Invoices', 'AllTimeInvoiceTotals'],
    }),
    previewBulkOnDemandPayout: build.query<api.BulkAcceleratedPayoutResponse, { vendorId: api.UUID }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewBulkOnDemandPayout(vendorId);
        return { data };
      },
      providesTags: ['BulkAcceleratedPayout'],
    }),
    confirmBulkOnDemandPayout: build.mutation<api.BulkAcceleratedPayoutResponse, api.ConfirmOnDemandPayoutRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmBulkOnDemandPayout(vendorId, request);

        return { data };
      },
      invalidatesTags: ['Invoices', 'BulkAcceleratedPayout', 'AllTimeInvoiceTotals'],
    }),
    previewSelectiveOnDemandPayout: build.query<api.SelectiveAcceleratedPayoutResponse, api.SelectiveAcceleratedPayoutRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.previewSelectiveOnDemandPayout(vendorId, request);
        return { data };
      },
      providesTags: ['SelectiveAcceleratedPayout'],
    }),
    confirmSelectiveOnDemandPayout: build.mutation<api.SelectiveAcceleratedPayoutResponse, api.ConfirmSelectiveOnDemandPayoutRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmSelectiveOnDemandPayout(vendorId, request);

        return { data };
      },
      invalidatesTags: ['Invoices', 'SelectiveAcceleratedPayout', 'AllTimeInvoiceTotals'],
    }),
    getPromotionalPayoutPreview: build.query<api.PromotionalPayoutPreviewResponse | undefined, { promotionId: api.UUID; vendorId: api.UUID }>({
      async queryFn({ promotionId, vendorId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getPromotionalPayoutPreview({ promotionId, vendorId });
        return { data };
      },
      providesTags: ['PromotionalPayout'],
    }),
    confirmPromotionalPayout: build.mutation<api.AcceleratedPayoutResponse, { vendorId: api.UUID; invoiceId: api.UUID; promotionId: api.UUID }>({
      async queryFn({ vendorId, invoiceId, promotionId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.confirmPromotionalPayout(vendorId, { invoiceId, promotionId });
        return { data };
      },
      invalidatesTags: ['Invoices', 'AllTimeInvoiceTotals'],
    }),
    getVendorUnderwritingTotals: build.query<api.VendorUnderwritingTotalsResponse | undefined, { vendorId: api.UUID }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getVendorUnderwritingTotals(vendorId);
        return { data };
      },
    }),
    getAllVendorTransfers: build.query<api.IPagedResults<api.RedactedVendorTransferResponse>, api.VendorTransferFilterParams>({
      async queryFn(params, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getAllVendorTransfers(params);
        return { data };
      },
    }),
    uploadInvoiceDocument: build.mutation<api.RedactedInvoiceDocumentResponse, {
      invoiceId: string,
      file: File,
    }>({
      query({ invoiceId, file }) {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return {
          url: `/invoices/${invoiceId}/documents`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Invoices'],
    }),
    getVendorInvoiceQuarantines: build.query<readonly api.RedactedVendorInvoiceQuarantineResponse[], { vendorId: api.UUID }>({
      async queryFn({ vendorId }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);
        const data = await externalInvoiceApi.getVendorInvoiceQuarantines<api.RedactedVendorInvoiceQuarantineResponse>(vendorId);
        return { data };
      },
      providesTags: ['VendorInvoiceQuarantines'],
    }),
    submitVendorInvoiceQuarantineCorrections: build.mutation<readonly api.RedactedInvoiceQuarantineResponse[], api.SubmitInvoiceQuarantineCorrectionsRequest & { vendorId: api.UUID }>({
      async queryFn({ vendorId, ...request }, { dispatch }) {
        const externalInvoiceApi = useInvoiceApi(dispatch);

        try {
          const data = await externalInvoiceApi.submitVendorInvoiceQuarantineCorrections<api.RedactedInvoiceQuarantineResponse>(vendorId, request);
          return { data };
        } catch (error) {
          // returned rather than thrown so a failed submit still invalidates the tags and refetches the stale list
          return { error: { status: 'CUSTOM_ERROR', error: error instanceof Error ? error.message : 'Unknown error' } };
        }
      },
      invalidatesTags: ['VendorInvoiceQuarantines', 'Invoices'],
    }),
  }),
});

export const {
  useConfirmBulkInstantPayoutMutation,
  useConfirmBulkOnDemandPayoutMutation,
  useConfirmPayOnApprovalByRequestMutation,
  useConfirmInstantPayoutMutation,
  useConfirmOnDemandPayoutMutation,
  useConfirmSelectiveInstantPayoutMutation,
  useConfirmSelectiveOnDemandPayoutMutation,
  useSubmitVendorInvoiceQuarantineCorrectionsMutation,
  useGetAllInvoiceAdjustmentsQuery,
  useGetAllInvoiceLineItemsQuery,
  useGetAllInvoicesQuery,
  useGetAllTimeInvoiceTotalsQuery,
  useGetInvoiceQuery,
  useGetVendorInvoiceQuarantinesQuery,
  useGetVendorClientTotalsQuery,
  useGetVendorUnderwritingTotalsQuery,
  useLazyGetAllInvoiceAdjustmentsQuery,
  useLazyGetAllInvoicesQuery,
  useLazyPreviewBulkOnDemandPayoutQuery,
  useLazyPreviewSelectiveOnDemandPayoutQuery,
  usePreviewBulkInstantPayoutQuery,
  usePreviewBulkOnDemandPayoutQuery,
  usePreviewPayOnApprovalByRequestQuery,
  usePreviewInstantPayoutQuery,
  usePreviewOnDemandPayoutQuery,
  usePreviewSelectiveInstantPayoutQuery,
  usePreviewSelectiveOnDemandPayoutQuery,
  useGetAllVendorTransfersQuery,
  useUploadInvoiceDocumentMutation,
  useGetPromotionalPayoutPreviewQuery,
  useConfirmPromotionalPayoutMutation,
} = invoicesApi;

export const exportInvoices = async (filters: api.ExportInvoiceFilterParams, exportFormat: ExportFormatType = 'vendor'): Promise<BlobPart> => {
  const params = new URLSearchParams();
  params.append('exportFormat', exportFormat);

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoices/csv/export?${params.toString()}`);

  Object.entries(filters).forEach(([k, v]) => {
    if (v) {
      if (Array.isArray(v)) {
        (v as Array<any>).forEach((arrayValue) => {
          url.searchParams.append(k, arrayValue);
        });
      } else {
        url.searchParams.append(k, v.toString());
      }
    }
  });
  const headers = await prepareHeaders(new Headers());
  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers,
  });

  return await response.blob();
};

export const exportInvoiceLineItems = async (filters: Pick<api.ExportInvoiceLineItemFilterParams, 'vendorId' | 'csvFormat' | 'batchTransferId'>, exportFormat: ExportFormatType = 'vendor'): Promise<BlobPart> => {
  const params = new URLSearchParams();
  params.append('exportFormat', exportFormat);

  const url = new URL(`${CONFIG.api.invoiceUrl}/invoice-line-items/csv/export?${params.toString()}`);

  Object.entries(filters).forEach(([k, v]) => {
    if (v) {
      if (Array.isArray(v)) {
        (v as Array<any>).forEach((arrayValue) => {
          url.searchParams.append(k, arrayValue);
        });
      } else {
        url.searchParams.append(k, v.toString());
      }
    }
  });
  const headers = await prepareHeaders(new Headers());
  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    method: 'GET',
    headers,
  });

  return await response.blob();
};
